'use client'

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import Layout from "@/components/elements/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { getStartupEvents, getStartupEventsByUser } from '@/services/api/StartupEvent';
import Image from 'next/image';
import DummyLogo from "@/assets/images/lucidity-placeholder.png";
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, getMonth, getYear } from 'date-fns';
import bgHero from "@/assets/images/bg-hero.png";
import { Pencil, Clock, MapPin, Warning, CalendarX, Calendar, GridFour, CaretLeft, CaretRight } from '@phosphor-icons/react';
import { useAtom } from 'jotai';
import { userAtom } from '@/Atoms';
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import useAuth from '@/hooks/auth';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Pagination, 
  PaginationContent, 
  PaginationItem, 
  PaginationLink, 
  PaginationNext, 
  PaginationPrevious 
} from "@/components/ui/pagination";

const CalendarView = ({ events, selectedYear, onYearChange, user, upcomingEvents }) => {
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [hoveredMonth, setHoveredMonth] = useState(null);
  const selectedMonthEventsRef = useRef(null);
  
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Group events by month
  const eventsByMonth = events.reduce((acc, event) => {
    const month = getMonth(parseISO(event.date));
    if (!acc[month]) acc[month] = [];
    acc[month].push(event);
    return acc;
  }, {});

  // Get event count for each month
  const getEventCount = (monthIndex) => {
    return eventsByMonth[monthIndex]?.length || 0;
  };

  // Get heat level based on event count
  const getHeatLevel = (count) => {
    if (count === 0) return 'none';
    if (count <= 2) return 'low';
    if (count <= 5) return 'medium';
    if (count <= 10) return 'high';
    return 'very-high';
  };

  // Get heat color classes
  const getHeatColor = (level) => {
    switch (level) {
      case 'none': return 'bg-gray-100 hover:bg-gray-200 border-gray-200';
      case 'low': return 'bg-blue-100 hover:bg-blue-200 border-blue-200';
      case 'medium': return 'bg-blue-300 hover:bg-blue-400 border-blue-300';
      case 'high': return 'bg-blue-500 hover:bg-blue-600 border-blue-500 text-white';
      case 'very-high': return 'bg-blue-700 hover:bg-blue-800 border-blue-700 text-white';
      default: return 'bg-gray-100 hover:bg-gray-200 border-gray-200';
    }
  };

  const handleMonthClick = (monthIndex) => {
    const newSelectedMonth = selectedMonth === monthIndex ? null : monthIndex;
    setSelectedMonth(newSelectedMonth);
    
    // Scroll to selected month events section if a month is selected
    if (newSelectedMonth !== null) {
      setTimeout(() => {
        if (selectedMonthEventsRef.current) {
          selectedMonthEventsRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          });
        }
      }, 100);
    }
  };

  const selectedMonthEvents = selectedMonth !== null ? eventsByMonth[selectedMonth] || [] : [];

  return (
    <div className="space-y-8">
      {/* Year Navigation */}
      <div className="flex items-center justify-center space-x-8 mb-8">
                 <Button
           variant="outline"
           onClick={() => onYearChange(selectedYear - 1)}
           className="p-2 hover:bg-gray-800 hover:text-white"
         >
           <CaretLeft className="w-5 h-5" />
         </Button>
        <h2 className="text-4xl font-bold text-white">{selectedYear}</h2>
                 <Button
           variant="outline"
           onClick={() => onYearChange(selectedYear + 1)}
           className="p-2 hover:bg-gray-800 hover:text-white"
         >
           <CaretRight className="w-5 h-5" />
         </Button>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {months.map((month, index) => {
          const eventCount = getEventCount(index);
          const heatLevel = getHeatLevel(eventCount);
          const heatColor = getHeatColor(heatLevel);
          const isSelected = selectedMonth === index;
          const isHovered = hoveredMonth === index;

          return (
            <Card
              key={month}
              className={`cursor-pointer transition-all duration-300 transform hover:scale-105 border-2 ${
                isSelected ? 'ring-2 ring-blue-400 shadow-lg' : ''
              } ${heatColor}`}
              onClick={() => handleMonthClick(index)}
              onMouseEnter={() => setHoveredMonth(index)}
              onMouseLeave={() => setHoveredMonth(null)}
            >
              <CardContent className="p-6 text-center">
                <div className="space-y-3">
                  <h3 className={`text-xl font-semibold ${
                    heatLevel === 'high' || heatLevel === 'very-high' ? 'text-white' : 'text-gray-700'
                  }`}>
                    {month}
                  </h3>
                  <div className={`text-3xl font-bold ${
                    heatLevel === 'high' || heatLevel === 'very-high' ? 'text-white' : 'text-blue-600'
                  }`}>
                    {eventCount}
                  </div>
                  <div className={`text-sm ${
                    heatLevel === 'high' || heatLevel === 'very-high' ? 'text-white/80' : 'text-gray-500'
                  }`}>
                    {eventCount === 1 ? 'event' : 'events'}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Heat Map Legend */}
      <div className="flex items-center justify-center space-x-4 mt-8">
        <span className="text-sm text-gray-400">Less</span>
        <div className="flex space-x-1">
          {['none', 'low', 'medium', 'high', 'very-high'].map((level) => (
            <div
              key={level}
              className={`w-4 h-4 rounded-sm ${getHeatColor(level)}`}
              title={`${level} activity`}
            />
          ))}
        </div>
        <span className="text-sm text-gray-400">More</span>
      </div>

      {/* Ongoing & Upcoming Events (when no month selected) */}
      {selectedMonth === null && (
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-white mb-2 text-center">
            Ongoing & Upcoming Events ({upcomingEvents.length})
          </h3>
          <p className="text-gray-400 text-center mb-6">
            Click on a month above to see events for that specific period
          </p>
          
          {upcomingEvents.length > 0 ? (
                         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
               {upcomingEvents.map((event) => (
                 <Link href={`/startup-events/${event.id}`} key={event.id}>
                   <Card className="bg-white shadow-lg rounded-xl overflow-hidden transition-all duration-300 hover:shadow-2xl transform hover:-translate-y-1 cursor-pointer h-[350px] flex flex-col group relative">
                     {(user?.id === event.user_id || user?.roles?.includes('admin-team')) && (
                       <div className="absolute top-3 right-3 z-10">
                         {!event.is_published ? (
                           <Badge className="bg-white/90 backdrop-blur-sm text-gray-900 border-0 shadow-lg hover:bg-white/95 transition-all duration-300 px-3 py-1">
                             <Clock weight="bold" className="w-3.5 h-3.5 mr-1.5" />
                             Draft
                           </Badge>
                         ) : !event.is_approved ? (
                           <Badge className="bg-yellow-300/90 backdrop-blur-sm text-gray-900 border-0 shadow-lg hover:bg-yellow-300/95 transition-all duration-300 px-3 py-1">
                             <Warning weight="bold" className="w-3.5 h-3.5 mr-1.5" />
                             Pending
                           </Badge>
                         ) : !user?.roles?.includes('admin-team') && (
                           <Badge className="bg-white/90 backdrop-blur-sm text-gray-900 border-0 shadow-lg hover:bg-white/95 transition-all duration-300 px-3 py-1">
                             <Pencil weight="bold" className="w-3.5 h-3.5 mr-1.5" />
                             My Event
                           </Badge>
                         )}
                       </div>
                     )}
                     <div className="relative h-32 w-full flex-shrink-0 overflow-hidden">
                      <Image
                        src={event.image_url || DummyLogo}
                        alt={event.name}
                        layout="fill"
                        objectFit="cover"
                        className="transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                      <div className="absolute bottom-2 left-3 right-3">
                        <div className="flex items-center text-xs text-white/90 mb-1 font-medium">
                          <Calendar className="w-3 h-3 mr-1" />
                          <span>{format(parseISO(event.date), 'MMM d, yyyy')}</span>
                        </div>
                      </div>
                    </div>
                    <CardContent className="p-4 flex-grow space-y-3">
                      <h4 className="text-lg font-bold text-gray-800 line-clamp-1">{event.name}</h4>
                      <p className="text-gray-600 text-sm line-clamp-2 leading-relaxed">
                        {event.short_description}
                      </p>
                      <div className="flex items-center text-sm text-gray-500">
                        <MapPin className="w-4 h-4 mr-1.5 flex-shrink-0 text-gray-400" />
                        <span className="line-clamp-1 font-medium">{event.city}, {event.country}</span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <CalendarX className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No upcoming events found for {selectedYear}</p>
            </div>
          )}
        </div>
      )}

      {/* Selected Month Events */}
      {selectedMonth !== null && (
        <div className="mt-12" ref={selectedMonthEventsRef}>
          <h3 className="text-2xl font-bold text-white mb-6 text-center">
            {months[selectedMonth]} {selectedYear} Events ({selectedMonthEvents.length})
          </h3>
          
          {selectedMonthEvents.length > 0 ? (
                         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
               {selectedMonthEvents.map((event) => (
                 <Link href={`/startup-events/${event.id}`} key={event.id}>
                   <Card className="bg-white shadow-lg rounded-xl overflow-hidden transition-all duration-300 hover:shadow-2xl transform hover:-translate-y-1 cursor-pointer h-[350px] flex flex-col group relative">
                     {(user?.id === event.user_id || user?.roles?.includes('admin-team')) && (
                       <div className="absolute top-3 right-3 z-10">
                         {!event.is_published ? (
                           <Badge className="bg-white/90 backdrop-blur-sm text-gray-900 border-0 shadow-lg hover:bg-white/95 transition-all duration-300 px-3 py-1">
                             <Clock weight="bold" className="w-3.5 h-3.5 mr-1.5" />
                             Draft
                           </Badge>
                         ) : !event.is_approved ? (
                           <Badge className="bg-yellow-300/90 backdrop-blur-sm text-gray-900 border-0 shadow-lg hover:bg-yellow-300/95 transition-all duration-300 px-3 py-1">
                             <Warning weight="bold" className="w-3.5 h-3.5 mr-1.5" />
                             Pending
                           </Badge>
                         ) : !user?.roles?.includes('admin-team') && (
                           <Badge className="bg-white/90 backdrop-blur-sm text-gray-900 border-0 shadow-lg hover:bg-white/95 transition-all duration-300 px-3 py-1">
                             <Pencil weight="bold" className="w-3.5 h-3.5 mr-1.5" />
                             My Event
                           </Badge>
                         )}
                       </div>
                     )}
                     <div className="relative h-32 w-full flex-shrink-0 overflow-hidden">
                      <Image
                        src={event.image_url || DummyLogo}
                        alt={event.name}
                        layout="fill"
                        objectFit="cover"
                        className="transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                      <div className="absolute bottom-2 left-3 right-3">
                        <div className="flex items-center text-xs text-white/90 mb-1 font-medium">
                          <Calendar className="w-3 h-3 mr-1" />
                          <span>{format(parseISO(event.date), 'MMM d, yyyy')}</span>
                        </div>
                      </div>
                    </div>
                    <CardContent className="p-4 flex-grow space-y-3">
                      <h4 className="text-lg font-bold text-gray-800 line-clamp-1">{event.name}</h4>
                      <p className="text-gray-600 text-sm line-clamp-2 leading-relaxed">
                        {event.short_description}
                      </p>
                      <div className="flex items-center text-sm text-gray-500">
                        <MapPin className="w-4 h-4 mr-1.5 flex-shrink-0 text-gray-400" />
                        <span className="line-clamp-1 font-medium">{event.city}, {event.country}</span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <CalendarX className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No events found for {months[selectedMonth]} {selectedYear}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function StartupEvents() {
  const [events, setEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [userState, setUserState] = useAtom(userAtom);
  const [activeFilter, setActiveFilter] = useState('approved_published');
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'grid'
  const { user } = useAuth();

  // Pagination state for upcoming events
  const [upcomingCurrentPage, setUpcomingCurrentPage] = useState(1);
  const [upcomingPaginationData, setUpcomingPaginationData] = useState(null);
  const [upcomingLoading, setUpcomingLoading] = useState(false);

  // Pagination state for past events
  const [pastCurrentPage, setPastCurrentPage] = useState(1);
  const [pastPaginationData, setPastPaginationData] = useState(null);
  const [pastLoading, setPastLoading] = useState(false);

  // Ref for past events section
  const pastEventsRef = useRef(null);
  
  // Ref for upcoming events section
  const upcomingEventsRef = useRef(null);

  const perPage = 12; // Number of events per page

  // Auto-set year based on view mode
  useEffect(() => {
    if (viewMode === 'calendar') {
      setSelectedYear(new Date().getFullYear());
    } else {
      setSelectedYear(null); // "All Years" for grid view
    }
  }, [viewMode]);

  // Fetch events for calendar view
  useEffect(() => {
    async function fetchAllEvents() {
      try {
        let response;
        const calendarYear = selectedYear || new Date().getFullYear();
        if (activeFilter === 'my_events' && user?.id) {
          response = await getStartupEventsByUser({
            params: {
              year: calendarYear,
              per_page: 1000 // Get all events for calendar
            }
          });
        } else {
          response = await getStartupEvents({
            params: { 
              filter: activeFilter,
              year: calendarYear,
              per_page: 1000 // Get all events for calendar
            }
          });
        }
        setAllEvents(response.data.data);
      } catch (error) {
        console.error('Failed to fetch all events:', error);
        setAllEvents([]);
      }
    }
    fetchAllEvents();
  }, [activeFilter, user, selectedYear]);

  // Fetch upcoming events for calendar view (independent of year selection)
  useEffect(() => {
    async function fetchUpcomingEvents() {
      try {
        let response;
        if (activeFilter === 'my_events' && user?.id) {
          response = await getStartupEventsByUser({
            params: {
              type: 'future',
              per_page: 1000 // Get all upcoming events
            }
          });
        } else {
          response = await getStartupEvents({
            params: { 
              filter: activeFilter,
              type: 'future',
              per_page: 1000 // Get all upcoming events
            }
          });
        }
        setUpcomingEvents(response.data.data);
      } catch (error) {
        console.error('Failed to fetch upcoming events:', error);
        setUpcomingEvents([]);
      }
    }
    fetchUpcomingEvents();
  }, [activeFilter, user]);

  useEffect(() => {
    if (viewMode === 'grid') {
      async function fetchEvents() {
        setUpcomingLoading(true);
        try {
          let response;
          if (activeFilter === 'my_events' && user?.id) {
            response = await getStartupEventsByUser({
              params: {
                page: upcomingCurrentPage,
                per_page: perPage,
                type: 'future'
              }
            });
          } else {
            response = await getStartupEvents({
              params: { 
                filter: activeFilter,
                type: 'future',
                page: upcomingCurrentPage,
                per_page: perPage
              }
            });
          }
          setEvents(response.data.data);
          setUpcomingPaginationData(response.data.meta);
        } catch (error) {
          console.error('Failed to fetch events:', error);
          setEvents([]);
          setUpcomingPaginationData(null);
        } finally {
          setUpcomingLoading(false);
        }
      }
      fetchEvents();
    }
  }, [activeFilter, user, upcomingCurrentPage, viewMode]);

  useEffect(() => {
    if (viewMode === 'grid') {
      async function fetchPastEvents() {
        setPastLoading(true);
        try {
          const response = await getStartupEvents({
            params: {
              filter: 'approved_published',
              type: 'past',
              year: selectedYear,
              page: pastCurrentPage,
              per_page: perPage
            }
          });
          setPastEvents(response.data.data);
          setPastPaginationData(response.data.meta);
        } catch (error) {
          console.error('Failed to fetch past events:', error);
          setPastEvents([]);
          setPastPaginationData(null);
        } finally {
          setPastLoading(false);
        }
      }
      fetchPastEvents();
    }
  }, [selectedYear, pastCurrentPage, viewMode]);

  const currentYear = new Date().getFullYear();
  const yearOptions = ['All Years', ...Array.from({ length: 5 }, (_, i) => (currentYear - i).toString())];

  const handleUpcomingPageChange = (page) => {
    setUpcomingCurrentPage(page);
    if (upcomingEventsRef.current) {
      upcomingEventsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handlePastPageChange = (page) => {
    setPastCurrentPage(page);
    if (pastEventsRef.current) {
      pastEventsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    setUpcomingCurrentPage(1);
  };

  const handleYearChange = (year) => {
    if (viewMode === 'calendar') {
      setSelectedYear(year);
    } else {
      setSelectedYear(year === 'All Years' ? null : year);
      setPastCurrentPage(1);
    }
  };

  const handleGridYearChange = (year) => {
    setSelectedYear(year === 'All Years' ? null : year);
    setPastCurrentPage(1);
  };

  const renderPagination = (currentPage, paginationData, onPageChange) => {
    if (!paginationData || paginationData.last_page <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    const totalPages = paginationData.last_page;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      pages.push(1);
      if (startPage > 2) {
        pages.push('...');
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push('...');
      }
      pages.push(totalPages);
    }

    return (
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-8 px-4">
        <div className="text-sm text-gray-400 order-2 sm:order-1">
          Showing <span className="font-medium text-white">{paginationData.from || 0}</span> to{' '}
          <span className="font-medium text-white">{paginationData.to || 0}</span> of{' '}
          <span className="font-medium text-white">{paginationData.total || 0}</span> events
        </div>
        
        <div className="flex items-center gap-2 order-1 sm:order-2">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious 
                  onClick={() => currentPage > 1 && onPageChange(currentPage - 1)}
                  className={`cursor-pointer ${
                    currentPage === 1 
                      ? 'pointer-events-none opacity-50' 
                      : 'hover:bg-gray-800 hover:text-white'
                  }`}
                />
              </PaginationItem>
              
              {pages.map((page, index) => (
                <PaginationItem key={index}>
                  {page === '...' ? (
                    <span className="flex h-9 w-9 items-center justify-center text-gray-400">
                      ...
                    </span>
                  ) : (
                    <PaginationLink
                      onClick={() => onPageChange(page)}
                      isActive={currentPage === page}
                      className={`cursor-pointer ${
                        currentPage === page 
                          ? 'bg-blue-600 text-white hover:bg-blue-700' 
                          : 'hover:bg-gray-800 hover:text-white'
                      }`}
                    >
                      {page}
                    </PaginationLink>
                  )}
                </PaginationItem>
              ))}
              
              <PaginationItem>
                <PaginationNext 
                  onClick={() => currentPage < totalPages && onPageChange(currentPage + 1)}
                  className={`cursor-pointer ${
                    currentPage === totalPages 
                      ? 'pointer-events-none opacity-50' 
                      : 'hover:bg-gray-800 hover:text-white'
                  }`}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
        
        <div className="text-sm text-gray-400 order-3 sm:hidden">
          Page <span className="font-medium text-white">{currentPage}</span> of{' '}
          <span className="font-medium text-white">{totalPages}</span>
        </div>
      </div>
    );
  };

  return (
    <Layout
      title="Startup Events"
      meta_title="Startup Events | Discover and Connect at Startup Gatherings"
      description="Discover and connect at the hottest startup gatherings. Browse upcoming events, create new ones, and network with fellow entrepreneurs and innovators."
    >
      <div className="relative">
        <div className="relative h-[400px]">
          <Image
            src={bgHero}
            alt="Startup Events Background"
            layout="fill"
            objectFit="cover"
            quality={100}
          />
          <div className="absolute inset-0 bg-black opacity-50"></div>
          <div className="relative container mx-auto h-full flex flex-col justify-center items-center p-8">
            <h1 className="text-4xl font-bold text-center mb-2 text-white">Startup Events</h1>
            <p className="text-xl text-center text-gray-200 mb-8">Discover and connect at the hottest startup gatherings</p>
            <Link href="/startup-events/create">
              <div className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-semibold py-2 px-8 rounded-full text-lg transition duration-300 shadow-lg hover:shadow-xl">
                Create New Startup Event
              </div>
            </Link>
          </div>
        </div>

        <div className="container mx-auto p-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
            {/* View Mode Toggle */}
            <div className="flex items-center space-x-4">
              <Tabs value={viewMode} onValueChange={setViewMode}>
                <TabsList className="grid w-full max-w-sm grid-cols-2 bg-gray-800/50 p-1 rounded-lg">
                  <TabsTrigger 
                    value="calendar"
                    className="data-[state=active]:bg-gray-900 data-[state=active]:text-white text-gray-400 flex items-center gap-2"
                  >
                    <Calendar className="w-4 h-4" />
                    Calendar
                  </TabsTrigger>
                                     <TabsTrigger 
                     value="grid"
                     className="data-[state=active]:bg-gray-900 data-[state=active]:text-white text-gray-400 flex items-center gap-2"
                   >
                     <GridFour className="w-4 h-4" />
                     Grid
                   </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {/* Filter Controls */}
            {user?.id && (
              <div className="flex flex-col sm:flex-row gap-4">
                <Tabs value={activeFilter} onValueChange={handleFilterChange}>
                  <TabsList className="grid w-full max-w-md grid-cols-3 bg-gray-800/50 p-1 rounded-lg">
                    <TabsTrigger 
                      value="approved_published"
                      className="data-[state=active]:bg-gray-900 data-[state=active]:text-white text-gray-400"
                    >
                      Public Events
                    </TabsTrigger>
                    {user?.roles?.includes('admin-team') ? (
                      <>
                        <TabsTrigger 
                          value="all"
                          className="data-[state=active]:bg-gray-900 data-[state=active]:text-white text-gray-400"
                        >
                          All Events
                        </TabsTrigger>
                        <TabsTrigger 
                          value="need_to_be_approved"
                          className="data-[state=active]:bg-gray-900 data-[state=active]:text-white text-gray-400"
                        >
                          Need Approval
                        </TabsTrigger>
                      </>
                    ) : (
                      <TabsTrigger 
                        value="my_events"
                        className="data-[state=active]:bg-gray-900 data-[state=active]:text-white text-gray-400"
                      >
                        My Events
                      </TabsTrigger>
                    )}
                  </TabsList>
                </Tabs>
              </div>
            )}
          </div>

          {/* Calendar View */}
          {viewMode === 'calendar' && (
                       <CalendarView 
             events={allEvents}
             selectedYear={selectedYear || new Date().getFullYear()}
             onYearChange={handleYearChange}
             user={user}
             upcomingEvents={upcomingEvents}
           />
          )}

          {/* Grid View */}
          {viewMode === 'grid' && (
            <>
              <div className="flex flex-col mb-8" ref={upcomingEventsRef}>
                <h2 className="text-3xl font-bold text-white text-center mt-8 mb-4">
                  Ongoing & Upcoming Events
                </h2>
                                 <div className="w-full flex justify-end mb-4">
                   <Select 
                     value={selectedYear ? selectedYear.toString() : 'All Years'} 
                     onValueChange={handleGridYearChange}
                   >
                     <SelectTrigger className="w-[180px]">
                       <SelectValue placeholder="Select Year" />
                     </SelectTrigger>
                     <SelectContent>
                       {yearOptions.map((year) => (
                         <SelectItem key={year} value={year}>
                           {year}
                         </SelectItem>
                       ))}
                     </SelectContent>
                   </Select>
                 </div>
              </div>

              {upcomingLoading ? (
                <div className="flex justify-center items-center py-16">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
                    {events.length > 0 ? (
                      events.map((event) => (
                        <Link href={`/startup-events/${event.id}`} key={event.id}>
                          <Card className="bg-white shadow-lg rounded-xl overflow-hidden transition-all duration-300 hover:shadow-2xl transform hover:-translate-y-1 cursor-pointer h-[420px] flex flex-col group relative">
                            {(user?.id === event.user_id || user?.roles?.includes('admin-team')) && (
                              <div className="absolute top-3 right-3 z-10">
                                {!event.is_published ? (
                                  <Badge className="bg-white/90 backdrop-blur-sm text-gray-900 border-0 shadow-lg hover:bg-white/95 transition-all duration-300 px-3 py-1">
                                    <Clock weight="bold" className="w-3.5 h-3.5 mr-1.5" />
                                    Draft
                                  </Badge>
                                ) : !event.is_approved ? (
                                  <Badge className="bg-yellow-300/90 backdrop-blur-sm text-gray-900 border-0 shadow-lg hover:bg-yellow-300/95 transition-all duration-300 px-3 py-1">
                                    <Warning weight="bold" className="w-3.5 h-3.5 mr-1.5" />
                                    Pending
                                  </Badge>
                                ) : !user?.roles?.includes('admin-team') && (
                                  <Badge className="bg-white/90 backdrop-blur-sm text-gray-900 border-0 shadow-lg hover:bg-white/95 transition-all duration-300 px-3 py-1">
                                    <Pencil weight="bold" className="w-3.5 h-3.5 mr-1.5" />
                                    My Event
                                  </Badge>
                                )}
                              </div>
                            )}
                            <div className="relative h-44 w-full flex-shrink-0 overflow-hidden">
                              <Image
                                src={event.image_url || DummyLogo}
                                alt={event.name}
                                layout="fill"
                                objectFit="cover"
                                className="transition-transform duration-500 group-hover:scale-110"
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                              <div className="absolute bottom-4 left-4 right-4">
                                <div className="flex items-center text-sm text-white/90 mb-1.5 font-medium">
                                  <svg className="w-4 h-4 mr-1.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                  <span>{format(parseISO(event.date), 'MMM d, yyyy')}</span>
                                </div>
                                <h2 className="text-xl font-bold text-white line-clamp-1 tracking-tight">{event.name}</h2>
                              </div>
                            </div>
                            <CardContent className="p-5 flex-grow space-y-4">
                              <p className="text-gray-600 text-sm line-clamp-3 leading-relaxed">
                                {event.short_description}
                              </p>
                              <div className="flex items-center text-sm text-gray-500">
                                <svg className="w-4 h-4 mr-1.5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <span className="line-clamp-1 font-medium">{event.city}, {event.country}</span>
                              </div>
                            </CardContent>
                          </Card>
                        </Link>
                      ))
                    ) : (
                      <div className="col-span-full flex flex-col items-center justify-center py-16 px-4">
                        <CalendarX weight="thin" className="w-16 h-16 text-gray-400 mb-4" />
                        <h3 className="text-xl font-semibold text-gray-700 mb-2">No Events Found</h3>
                        <p className="text-gray-500 text-center max-w-md mb-8">
                          {activeFilter === 'my_events' 
                            ? "You haven't created any events yet. Start by creating your first event!"
                            : "There are no events available at the moment. Check back later or create your own event!"}
                        </p>
                        <Link href="/startup-events/create">
                          <Button className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white">
                            Create New Event
                          </Button>
                        </Link>
                      </div>
                    )}
                  </div>

                  {/* Pagination for upcoming events */}
                  {renderPagination(upcomingCurrentPage, upcomingPaginationData, handleUpcomingPageChange)}
                </>
              )}

              {/* Past Events Section */}
              <div className="mt-16" ref={pastEventsRef}>
                <h2 className="text-3xl font-bold text-white text-center mb-8">
                  Past Events
                </h2>

                {pastLoading ? (
                  <div className="flex justify-center items-center py-16">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
                      {pastEvents.length > 0 ? (
                        pastEvents.map((event) => (
                          <Link href={`/startup-events/${event.id}`} key={event.id}>
                            <Card className="bg-white shadow-lg rounded-xl overflow-hidden transition-all duration-300 hover:shadow-2xl transform hover:-translate-y-1 cursor-pointer h-[420px] flex flex-col group relative">
                              {(user?.id === event.user_id || user?.roles?.includes('admin-team')) && (
                                <div className="absolute top-3 right-3 z-10">
                                  {!event.is_published ? (
                                    <Badge className="bg-white/90 backdrop-blur-sm text-gray-900 border-0 shadow-lg hover:bg-white/95 transition-all duration-300 px-3 py-1">
                                      <Clock weight="bold" className="w-3.5 h-3.5 mr-1.5" />
                                      Draft
                                    </Badge>
                                  ) : !event.is_approved ? (
                                    <Badge className="bg-yellow-300/90 backdrop-blur-sm text-gray-900 border-0 shadow-lg hover:bg-yellow-300/95 transition-all duration-300 px-3 py-1">
                                      <Warning weight="bold" className="w-3.5 h-3.5 mr-1.5" />
                                      Pending
                                    </Badge>
                                  ) : !user?.roles?.includes('admin-team') && (
                                    <Badge className="bg-white/90 backdrop-blur-sm text-gray-900 border-0 shadow-lg hover:bg-white/95 transition-all duration-300 px-3 py-1">
                                      <Pencil weight="bold" className="w-3.5 h-3.5 mr-1.5" />
                                      My Event
                                    </Badge>
                                  )}
                                </div>
                              )}
                              <div className="relative h-44 w-full flex-shrink-0 overflow-hidden">
                                <Image
                                  src={event.image_url || DummyLogo}
                                  alt={event.name}
                                  layout="fill"
                                  objectFit="cover"
                                  className="transition-transform duration-500 group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                                <div className="absolute bottom-4 left-4 right-4">
                                  <div className="flex items-center text-sm text-white/90 mb-1.5 font-medium">
                                    <svg className="w-4 h-4 mr-1.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    <span>{format(parseISO(event.date), 'MMM d, yyyy')}</span>
                                  </div>
                                  <h2 className="text-xl font-bold text-white line-clamp-1 tracking-tight">{event.name}</h2>
                                </div>
                              </div>
                              <CardContent className="p-5 flex-grow space-y-4">
                                <p className="text-gray-600 text-sm line-clamp-3 leading-relaxed">
                                  {event.short_description}
                                </p>
                                <div className="flex items-center text-sm text-gray-500">
                                  <svg className="w-4 h-4 mr-1.5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                  </svg>
                                  <span className="line-clamp-1 font-medium">{event.city}, {event.country}</span>
                                </div>
                              </CardContent>
                            </Card>
                          </Link>
                        ))
                      ) : (
                        <div className="col-span-full flex flex-col items-center justify-center py-16 px-4">
                          <CalendarX weight="thin" className="w-16 h-16 text-gray-400 mb-4" />
                          <h3 className="text-xl font-semibold text-gray-700 mb-2">No Past Events Found</h3>
                          <p className="text-gray-500 text-center max-w-md">
                            There are no past events available for {selectedYear || 'the selected period'}.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Pagination for past events */}
                    {renderPagination(pastCurrentPage, pastPaginationData, handlePastPageChange)}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}